import { AnyAction, ListenerEffectAPI, ListenerMiddlewareInstance, ThunkDispatch } from '@reduxjs/toolkit'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { DeployedEnv } from '@typesApp/Config'
import { config } from '@config/config'
import {
  pollClaimRecordController,
  PollClaimRecordControllerReturn,
} from '@hooks/poll-claimrecord/pollClaimRecordController'
import { authApi } from '@redux/queries/auth'
import { claimRecordsApi } from '@redux/queries/claimRecords'
import { unwrapQueryActionResult } from '@redux/queries/unwrap'
import { setClaimRecordUpdatePollingInProgress } from '@redux/slices/connectorSlice'
import { AppState } from '@redux/store'
import { logger } from '@utils/logger'
import { isProductUpdateRequired, isPollCartStatus } from './claimRecordUpdateUtil'

const POLLING = {
  pollingInterval: 1000,
  maxWaitTime: 30000,
  maxWaitTimePreprod: 120000,
}

type ListenerApi = ListenerEffectAPI<unknown, ThunkDispatch<unknown, unknown, AnyAction>, unknown>
type ListenerInstance = ListenerMiddlewareInstance<unknown, ThunkDispatch<unknown, unknown, AnyAction>, unknown>

/**
 * Poll claim record on token fetch to trigger claim record updates (e.g. product updates) if needed
 * This is needed because the claim record may not be ready when the token is fetched, and we need to wait for it to be ready before we can perform updates
 * The claim record update listener will handle the actual updates when the claim record is fetched again
 * The polling will stop when the claim record is ready or when the max wait time is exceeded
 * The max wait time is shorter in production to avoid long waits for users, but longer in pre-production to allow for slower environments
 */
export const initCartProductUpdateOnTokenFetchListener = (listenerMiddleware: ListenerInstance) => {
  listenerMiddleware.startListening({
    matcher: authApi.endpoints.getAuthToken.matchFulfilled,

    effect: async (action, listenerApi) => {
      const authToken = action.payload
      const state = listenerApi.getState() as AppState
      const isInProgress =
        state.connector.isClaimRecordUpdateInProgress || state.connector.isClaimRecordUpdatePollingInProgress

      if (!authToken?.claimRecordId || isInProgress) return

      listenerApi.dispatch(setClaimRecordUpdatePollingInProgress(true))

      try {
        await pollThenUpdate(listenerApi, authToken.claimRecordId)
      } catch (error) {
        logger.warn('directbilling - update product error', error)
      } finally {
        listenerApi.dispatch(setClaimRecordUpdatePollingInProgress(false))
      }
    },
  })
}

const pollThenUpdate = async (listenerApi: ListenerApi, claimRecordId: string): Promise<void> => {
  const handlers = {
    isReady: (claimRecord: ClaimRecord) => {
      return !isPollCartStatus(claimRecord?.status) || isProductUpdateRequired(claimRecord.cartStatus)
    },
    onReady: async (_claimRecord: ClaimRecord) => {
      // NOTE: we don't need to perform the update here, as the claimRecordUpdateListener will handle it when the claim record is fetched again
      safeFetchClaimRecord(listenerApi, claimRecordId)
    },
    onTimeExceeded: (_claimRecord: ClaimRecord) => {
      // noop
    },
    onError: (error?: unknown) => {
      logger.warn('directbilling - polling error', error)
    },
  }

  const poller = getPollClaimRecordController(listenerApi, claimRecordId)
  await poller.start(handlers, claimRecordId)
}

const getPollClaimRecordController = (
  listenerApi: ListenerApi,
  claimRecordId: string
): PollClaimRecordControllerReturn => {
  // Note: using the claim record fetch that does not trigger the cartProductUpdateListener
  const service = {
    fetchClaimRecord: () => fetchClaimRecordForPolling(listenerApi, claimRecordId),
  }

  const maxWaitTime = config.getDeployedEnv() === DeployedEnv.PROD ? POLLING.maxWaitTime : POLLING.maxWaitTimePreprod

  const params = {
    maxWaitTime,
    pollingInterval: POLLING.pollingInterval,
  }

  return pollClaimRecordController(service, params)
}

const safeFetchClaimRecord = async (listenerApi: ListenerApi, claimRecordId: string): Promise<void> => {
  try {
    await unwrapQueryActionResult<ClaimRecord>(
      listenerApi.dispatch(claimRecordsApi.endpoints.getClaimRecord.initiate(claimRecordId, { forceRefetch: true }))
    )
  } catch (error) {
    logger.warn('directbilling - update products not performed, error fetching claim record', error)
  }
}

const fetchClaimRecordForPolling = async (listenerApi: ListenerApi, claimRecordId: string): Promise<ClaimRecord> => {
  return await unwrapQueryActionResult<ClaimRecord>(
    listenerApi.dispatch(
      claimRecordsApi.endpoints.getClaimRecordNoCache.initiate(claimRecordId, { forceRefetch: true })
    )
  )
}
